import { createContext, useCallback, useReducer, useContext, useEffect, useMemo } from 'react'
import { Milestone, ProgressBarType, MILESTONE_TYPES, Customizations, MilestoneCategory } from '@shopflo/ui'
import { compareStringConvertValue } from 'src/lib/utils/helpers'
import { successToast, errorToast } from 'src/lib/utils/toasters'
import {
  parseCartResponse,
  DEFAULT_PROGRESS_BAR,
  ProgressBarData,
  parseProgressBarForBackend,
  DEFAULT_PROGRESS_BAR_COUNT_BASED,
  DEFAULT_PROGRESS_BAR_AMOUNT_BASED
} from '@shopflo/ui'
import useConfirmOnRouteChanges from '../utils/custom-hooks/useConfirmOnRouteChanges'
import { useMerchant } from './MerchantContext'
import { useCheckoutUI } from './CheckoutUIContext'
import { BannerConfigType } from '../types/customizeUI'
import { CouponVisibilityStateConfigType, ThemeColorPaletteType } from '../types/merchantAttributes'
import { getUIAttributes, setMerchantAttributes, setUIAttributes } from '../core/api-methods'
import { useLocale } from '../hooks/useLocal'
import { DEFAULT_CART_NOTE_ERROR, DEFAULT_CART_NOTES, DEFAULT_CART_UI_DATA } from 'src/assets/CartUIConfigs'
import {
  createCartNotePayload,
  createCartUIPayload,
  handleCartNoteErrors,
  parseNewCartConfig
} from '../utils/cartUIHelpers'
import {
  Action,
  ActionType,
  CART_UPDATE_TYPE,
  CART_WIDTH_FOR_MOBILE,
  CartContextActions,
  CartContextGetters,
  CartContextState,
  CartNoteType,
  customizeCartUIType
} from '../types/cartUI'
import { useLoader } from './LoaderContext'

import { useActivityLogs, EntityName } from '../hooks/useActivityLogs'
import { createUIAttributePayload, SFContextType } from '../utils/attributesParsers'
import { CartNoteErrorType } from '../types/cart'
import { CART_SCROLLABLE_ELEMENTS_TYPE } from 'src/components/cartV2/config'

const cartState: CartContextState = {
  isEditingMilestone: false,
  draftMilestone: {
    milestone: 0,
    milestone_type: MILESTONE_TYPES.FREEBIE,
    milestone_header: 'This is a placeholder text with exactly fifty characters.',
    milestone_text: 'Free shipping',
    milestone_amount: 0
  },
  initialProgressBar: DEFAULT_PROGRESS_BAR,
  progressBar: DEFAULT_PROGRESS_BAR,
  countBasedProgressBar: DEFAULT_PROGRESS_BAR_COUNT_BASED,
  amountBasedProgressBar: DEFAULT_PROGRESS_BAR_AMOUNT_BASED,
  cartUIData: DEFAULT_CART_UI_DATA,
  initialCartUIData: DEFAULT_CART_UI_DATA,
  cartNotes: DEFAULT_CART_NOTES,
  initialCartNotes: DEFAULT_CART_NOTES,
  cartNoteErrors: DEFAULT_CART_NOTE_ERROR,
  showBannerErrors: false
}

const CartContext = createContext<{
  state: CartContextState
  actions: CartContextActions
  getters: CartContextGetters
}>({
  state: cartState,
  actions: {},
  getters: {}
} as { state: CartContextState; actions: CartContextActions; getters: CartContextGetters })

const cartContextReducer = (state: CartContextState, action: Action): CartContextState => {
  switch (action.type) {
    case ActionType.SET_AMOUNT_BASED_PROGRESS_BAR_AS_ACTIVE:
      return {
        ...state,
        progressBar: { ...state.amountBasedProgressBar }
      }
    case ActionType.SET_COUNT_BASED_PROGRESS_BAR_AS_ACTIVE:
      return {
        ...state,
        progressBar: { ...state.countBasedProgressBar }
      }
    case ActionType.SET_CART_CONTEXT:
      return { ...state, ...action.payload }
    case ActionType.SET_IS_PROGRESS_BAR_ENABLED:
      return { ...state, progressBar: { ...state.progressBar, isEnabled: action.payload } }
    case ActionType.SET_IS_EDITING_MILESTONE:
      return { ...state, isEditingMilestone: action.payload }
    case ActionType.SET_DRAFT_MILESTONE:
      return { ...state, draftMilestone: action.payload }
    case ActionType.SET_MILESTONES:
      return { ...state, progressBar: { ...state.progressBar, milestones: action.payload } }
    case ActionType.SET_CUSTOMIZATIONS:
      return { ...state, progressBar: { ...state.progressBar, customizations: action.payload } }
    case ActionType.SET_INITAL_PROGRESS_BAR_STATE:
      return { ...state, initialProgressBar: { ...state.progressBar, ...action.payload } }
    case ActionType.ADD_MILESTONE:
      return {
        ...state,
        progressBar: { ...state.progressBar, milestones: [...state.progressBar.milestones, action.payload] }
      }
    case ActionType.SAVE_PROGRESSBAR:
      return { ...state, initialProgressBar: state.progressBar }
    case ActionType.DISCARD_CHANGES:
      return {
        ...state,
        progressBar: state.initialProgressBar,
        cartNotes: state.initialCartNotes,
        cartNoteErrors: { ...DEFAULT_CART_NOTE_ERROR }
      }
    case ActionType.SET_INITIAL_CUSTOMIZE_UI_CART_DATA:
      return { ...state, initialCartUIData: action.payload }
    case ActionType.SET_CART_BANNER_CONFIG_DATA:
      return { ...state, cartUIData: { ...state.cartUIData, cartBannerUIConfigData: action.payload } }
    case ActionType.DISCARD_CART_UI_CHANGES:
      return { ...state, cartUIData: state.initialCartUIData }
    case ActionType.SAVE_CART_UI_CONFIG_DATA:
      return { ...state, initialCartUIData: state.cartUIData }
    case ActionType.SET_CART_WIDTH:
      return { ...state, cartUIData: { ...state.cartUIData, cartWidthForMobile: action.payload } }
    case ActionType.SET_CART_BOTTOM_BANNER_CONFIG_DATA:
      return { ...state, cartUIData: { ...state.cartUIData, cartBottomBannerUIConfigData: action.payload } }
    case ActionType.SET_CART_NOTES:
      return { ...state, cartNotes: action.payload }
    case ActionType.SET_INITIAL_CART_NOTES:
      return { ...state, initialCartNotes: action.payload }
    case ActionType.SET_CART_NOTE_ERROR:
      return { ...state, cartNoteErrors: action.payload }
    case ActionType.SET_SHOW_BANNER_ERRORS:
      return { ...state, showBannerErrors: action.payload }
    case ActionType.SET_CART_COUPON_VISIBILITY_CONFIG:
      return {
        ...state,
        cartUIData: {
          ...state.cartUIData,
          couponVisibilityConfig: action.payload
        }
      }
    case ActionType.SET_CART_LAYOUT:
      return { ...state, cartUIData: { ...state.cartUIData, page: { ...state.cartUIData.page, ...action.payload } } }
    case ActionType.SET_CART_HEADER:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, header: { ...state.cartUIData.header, ...action.payload } }
      }
    case ActionType.SET_CART_FOOTER:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, footer: { ...state.cartUIData.footer, ...action.payload } }
      }
    case ActionType.SET_CART_PRIMARY_CTA_CONFIG:
      return {
        ...state,
        cartUIData: {
          ...state.cartUIData,
          primaryCTAConfig: { ...state.cartUIData.primaryCTAConfig, ...action.payload }
        }
      }
    case ActionType.SET_CART_ADDON_CONFIG:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, addonConfig: { ...state.cartUIData.addonConfig, ...action.payload } }
      }
    case ActionType.SET_CART_ELEMENT_ORDER:
      return {
        ...state,
        cartUIData: {
          ...state.cartUIData,
          elementOrder: [...(action.payload.elementOrder as CART_SCROLLABLE_ELEMENTS_TYPE[])]
        }
      }
    case ActionType.SET_CART_ORDER_SUMMARY_CONFIG:
      return {
        ...state,
        cartUIData: {
          ...state.cartUIData,
          orderSummaryConfig: { ...state.cartUIData.orderSummaryConfig, ...action.payload }
        }
      }
    case ActionType.SET_CART_DISCOUNT_CONFIG:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, discountConfig: { ...state.cartUIData.discountConfig, ...action.payload } }
      }
    case ActionType.SET_CART_UPSELL_CONFIG:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, upsell: { ...state.cartUIData.upsell, ...action.payload } }
      }
    case ActionType.SET_CART_NOTES_CONFIG:
      return {
        ...state,
        cartUIData: { ...state.cartUIData, notesConfig: { ...state.cartUIData.notesConfig, ...action.payload } }
      }
    case ActionType.SET_CART_COUPON_CARD_CONFIG:
      return {
        ...state,
        cartUIData: {
          ...state.cartUIData,
          couponPageConfig: { ...state.cartUIData.couponPageConfig, ...action.payload }
        }
      }
    case ActionType.SET_SHOW_UPI_ICONS_ON_CART_BUTTON:
      return { ...state, cartUIData: { ...state.cartUIData, showUpiIconsOnCartButton: action.payload } }
    case ActionType.SET_CART_MARKETING_FLAG:
      return { ...state, cartUIData: { ...state.cartUIData, enableMarketingFlag: action.payload } }
    default:
      return state
  }
}

const CartContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { sendActivityLog } = useActivityLogs()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const {
    getters: { checkoutUI }
  } = useCheckoutUI()
  const [state, dispatch] = useReducer(cartContextReducer, cartState)
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const setActiveProgressBar = useCallback((payload: MilestoneCategory) => {
    if (payload === MilestoneCategory.COUNT_BASED) {
      dispatch({ type: ActionType.SET_COUNT_BASED_PROGRESS_BAR_AS_ACTIVE })
    } else {
      dispatch({ type: ActionType.SET_AMOUNT_BASED_PROGRESS_BAR_AS_ACTIVE })
    }
  }, [])

  const setInitialProgressBarState = useCallback((payload: ProgressBarType) => {
    dispatch({ type: ActionType.SET_INITAL_PROGRESS_BAR_STATE, payload })
  }, [])

  const setCartContext = useCallback((context: CartContextState) => {
    dispatch({ type: ActionType.SET_CART_CONTEXT, payload: context })
  }, [])
  const setIsProgressBarEnabled = useCallback((isEnabled: boolean) => {
    dispatch({ type: ActionType.SET_IS_PROGRESS_BAR_ENABLED, payload: isEnabled })
  }, [])

  const setIsEditingMilestone = useCallback((isEditing: boolean) => {
    dispatch({ type: ActionType.SET_IS_EDITING_MILESTONE, payload: isEditing })
  }, [])

  const setDraftMilestone = useCallback((milestone: Milestone) => {
    dispatch({ type: ActionType.SET_DRAFT_MILESTONE, payload: milestone })
  }, [])

  const setMilestones = useCallback((milestones: Milestone[]) => {
    dispatch({ type: ActionType.SET_MILESTONES, payload: milestones })
  }, [])

  const setCustomizations = useCallback((customizations: Customizations) => {
    dispatch({ type: ActionType.SET_CUSTOMIZATIONS, payload: customizations })
  }, [])

  const setCartBannerConfig = useCallback((payload: BannerConfigType) => {
    dispatch({ type: ActionType.SET_CART_BANNER_CONFIG_DATA, payload })
    dispatch({ type: ActionType.SET_SHOW_BANNER_ERRORS, payload: false })
  }, [])

  const setCartButtonCTAText = useCallback((text: string) => {
    dispatch({ type: ActionType.SET_CART_BUTTON_CTA_TEXT, payload: text })
  }, [])

  const setInitialCartData = useCallback((payload: customizeCartUIType) => {
    dispatch({ type: ActionType.SET_INITIAL_CUSTOMIZE_UI_CART_DATA, payload })
  }, [])

  const discardCartUIChanges = useCallback(() => {
    dispatch({ type: ActionType.DISCARD_CART_UI_CHANGES })
  }, [])

  const setCartWidthForMobile = useCallback((payload: CART_WIDTH_FOR_MOBILE) => {
    dispatch({ type: ActionType.SET_CART_WIDTH, payload })
  }, [])

  const setCartBottomBannerConfig = useCallback((payload: BannerConfigType) => {
    dispatch({ type: ActionType.SET_CART_BOTTOM_BANNER_CONFIG_DATA, payload })
  }, [])

  const setCartNotes = useCallback((payload: CartNoteType) => {
    dispatch({ type: ActionType.SET_CART_NOTES, payload })
  }, [])

  const setInitialCartNotes = useCallback((payload: CartNoteType) => {
    dispatch({ type: ActionType.SET_INITIAL_CART_NOTES, payload })
  }, [])

  const setCartNoteErrors = useCallback((payload: CartNoteErrorType) => {
    dispatch({ type: ActionType.SET_CART_NOTE_ERROR, payload })
  }, [])

  const setCartCouponVisibilityConfig = useCallback((payload: CouponVisibilityStateConfigType[]) => {
    dispatch({ type: ActionType.SET_CART_COUPON_VISIBILITY_CONFIG, payload })
  }, [])

  const setShowUpiIconsOnCartButton = useCallback((payload: boolean) => {
    dispatch({ type: ActionType.SET_SHOW_UPI_ICONS_ON_CART_BUTTON, payload })
  }, [])

  const setCartMarketingFlag = useCallback((payload: boolean) => {
    dispatch({ type: ActionType.SET_CART_MARKETING_FLAG, payload })
  }, [])

  const setCartUIConfigData = useCallback((payload: Record<string, unknown>, type: CART_UPDATE_TYPE) => {
    switch (type) {
      case 'LAYOUT':
        dispatch({ type: ActionType.SET_CART_LAYOUT, payload })
        break
      case 'HEADER':
        dispatch({ type: ActionType.SET_CART_HEADER, payload })
        break
      case 'FOOTER':
        dispatch({ type: ActionType.SET_CART_FOOTER, payload })
        break
      case 'PRIMARY_CTA_CONFIG':
        dispatch({ type: ActionType.SET_CART_PRIMARY_CTA_CONFIG, payload })
        break
      case 'ADDON_CONFIG':
        dispatch({ type: ActionType.SET_CART_ADDON_CONFIG, payload })
        break
      case 'ELEMENT_ORDER':
        dispatch({ type: ActionType.SET_CART_ELEMENT_ORDER, payload })
        break
      case 'ORDER_SUMMARY':
        dispatch({ type: ActionType.SET_CART_ORDER_SUMMARY_CONFIG, payload })
        break
      case 'DISCOUNT':
        dispatch({ type: ActionType.SET_CART_DISCOUNT_CONFIG, payload })
        break
      case 'UPSELL':
        dispatch({ type: ActionType.SET_CART_UPSELL_CONFIG, payload })
        break
      case 'NOTES':
        dispatch({ type: ActionType.SET_CART_NOTES_CONFIG, payload })
        break
      case 'COUPON_CARD':
        dispatch({ type: ActionType.SET_CART_COUPON_CARD_CONFIG, payload })
        break
      default:
        break
    }
  }, [])

  const addMilestone = (milestone: Milestone) => {
    const milestones = [...state.progressBar.milestones]
    const index = milestones.findIndex(m => m.milestone === milestone.milestone)
    if (index === -1) {
      milestones.push(milestone)
    } else {
      milestones[index] = milestone
    }
    setMilestones(milestones)
    setIsEditingMilestone(false)
    const activityLogPayload = {
      entityName: EntityName.CART,
      actionName: 'edited configuration'
    }
    sendActivityLog(activityLogPayload)
  }

  const saveProgressbar = async () => {
    const { hasError, cartNoteErrors } = handleCartNoteErrors(state.cartNotes)
    if (hasError) {
      setCartNoteErrors(cartNoteErrors)
      return
    }
    try {
      setLoaderProperties({
        isLoaderVisible: true
      })

      const promises = []

      if (getters.hasUnsavedCartWidgetChanges) {
        // Sync the active progressBar changes back to the correct state (count or amount based)
        const isCountBased = state.progressBar.customizations.category === MilestoneCategory.COUNT_BASED

        const countBasedPayload = parseProgressBarForBackend({
          ...(isCountBased ? state.progressBar : state.countBasedProgressBar),
          isEnabled: isCountBased
        })
        const amountBasedPayload = parseProgressBarForBackend({
          ...(!isCountBased ? state.progressBar : state.amountBasedProgressBar),
          isEnabled: !isCountBased
        })

        const payload = createUIAttributePayload(SFContextType.CART, {
          progress_bar_attributes: [countBasedPayload, amountBasedPayload]
        })

        const progressBarPromise = setUIAttributes(payload).then((response: any) => {
          if (response?.error || !Boolean(response?.data[0]?.ui_attributes?.progress_bar_attributes)) {
            throw response.error
          }

          handleProgressBarResponse(response?.data[0]?.ui_attributes)

          successToast('Progress bar saved successfully')
        })

        promises.push(progressBarPromise)
      }

      if (getters.hasUnsavedCartNoteChanges) {
        const cartNotesPayload = createCartNotePayload(state.cartNotes)

        const cartNotesPromise = setMerchantAttributes(cartNotesPayload, merchant?.accountId ?? '').then(
          (response: any) => {
            if (response?.error) {
              throw response.error
            }
            setMerchantData(response?.data)
            successToast('Cart notes saved successfully')
          }
        )

        promises.push(cartNotesPromise)
      }
      const activityLogPayload = {
        entityName: EntityName.CART_WIDGET_TAB,
        actionName: t('edit_cart_widget_configuration')
      }
      sendActivityLog(activityLogPayload)
      await Promise.all(promises)
    } catch (error) {
      errorToast('Failed to save changes')
    } finally {
      setLoaderProperties({
        isLoaderVisible: false
      })
    }
  }

  const discardChanges = useCallback(() => {
    dispatch({ type: ActionType.DISCARD_CHANGES })
  }, [])

  const saveCartUIConfigData = async () => {
    const stripHtml = (html: string | undefined) => (html ?? '').replace(/<[^>]*>/g, '').trim()
    const bannerData = state.cartUIData.cartBannerUIConfigData
    const hasEmptyBanner = bannerData.multipleBannersEnabled
      ? (bannerData.banners ?? []).some(b => !stripHtml(b.text))
      : false
    if (hasEmptyBanner) {
      // Removing the error toast as it was causing a double toast issue when saving the cart UI config data. The error message is now displayed in the banner control component itself.
      // errorToast(t('banner_text_empty_error'))
      dispatch({ type: ActionType.SET_SHOW_BANNER_ERRORS, payload: true })
      return
    }

    // Sync the active progressBar changes back to the correct state (count or amount based)
    const isCountBased = state.progressBar.customizations.category === MilestoneCategory.COUNT_BASED

    const countBasedPayload = parseProgressBarForBackend({
      ...(isCountBased ? state.progressBar : state.countBasedProgressBar),
      isEnabled: isCountBased ? state.progressBar.isEnabled : false
    })
    const amountBasedPayload = parseProgressBarForBackend({
      ...(!isCountBased ? state.progressBar : state.amountBasedProgressBar),
      isEnabled: !isCountBased ? state.progressBar.isEnabled : false
    })

    const cartPayload = createCartUIPayload(state.cartUIData)
    const payload = createUIAttributePayload(SFContextType.CART, {
      ...cartPayload,
      progress_bar_attributes: [countBasedPayload, amountBasedPayload]
    })
    try {
      setLoaderProperties({
        isLoaderVisible: true
      })
      const response: any = await setUIAttributes(payload)
      if (response?.error) {
        errorToast(response?.error)
      } else {
        const activityLogPayload = {
          entityName: EntityName.CART_CORE_TAB,
          actionName: t('edit_cart_core_setup_configuration')
        }
        sendActivityLog(activityLogPayload)
        successToast(t('alert_customization_saved'))
        const uiAttributes = response?.data[0]?.ui_attributes
        handleProgressBarResponse(uiAttributes, {
          cartUIData: parseNewCartConfig(uiAttributes, checkoutUI)
        })
        setInitialCartData(parseNewCartConfig(uiAttributes, checkoutUI))
      }
    } catch (error) {
      errorToast(t('alert_customization_failed'))
    } finally {
      setLoaderProperties({
        isLoaderVisible: false
      })
    }
  }

  const handleProgressBarResponse = (uiAttributes: any, additionalState?: Partial<CartContextState>) => {
    const { countBased, amountBased } = parseCartResponse(uiAttributes?.progress_bar_attributes as ProgressBarData[])
    const currentProgressBar = getters.isCountBased ? countBased : amountBased

    setCartContext({
      ...state,
      ...additionalState,
      progressBar: { ...state.progressBar, ...currentProgressBar },
      countBasedProgressBar: countBased,
      amountBasedProgressBar: amountBased
    })
    setInitialProgressBarState(currentProgressBar)
  }

  const initializeCartContext = async () => {
    try {
      const response: any = await getUIAttributes(SFContextType.CART)
      const { countBased, amountBased } = parseCartResponse(
        response?.data?.ui_attributes?.progress_bar_attributes ?? ([] as ProgressBarData[])
      )
      const activeProgressBar = countBased.isEnabled ? countBased : amountBased
      const cartUIData: customizeCartUIType = parseNewCartConfig(response?.data?.ui_attributes, checkoutUI)
      setCartContext({
        ...state,
        progressBar: { ...state.progressBar, ...activeProgressBar },
        countBasedProgressBar: countBased,
        amountBasedProgressBar: amountBased,
        cartUIData,
        cartNotes: merchant?.attributes?.cartNote ?? DEFAULT_CART_NOTES
      })
      setInitialCartData(cartUIData)
      setInitialProgressBarState(activeProgressBar)
      setInitialCartNotes(merchant?.attributes?.cartNote ?? DEFAULT_CART_NOTES)
    } catch (error) {
      errorToast('Failed to load cart configuration')
    }
  }

  useEffect(() => {
    if (!merchant?.attributes) return
    initializeCartContext()
  }, [merchant?.attributes])

  const actions = {
    setCartContext,
    setIsProgressBarEnabled,
    setIsEditingMilestone,
    addMilestone,
    setDraftMilestone,
    setMilestones,
    setCustomizations,
    saveProgressbar,
    discardChanges,
    setActiveProgressBar,
    setCartBannerConfig,
    setCartButtonCTAText,
    discardCartUIChanges,
    saveCartUIConfigData,
    setCartWidthForMobile,
    setCartBottomBannerConfig,
    setCartNotes,
    setInitialCartNotes,
    setCartNoteErrors,
    setCartCouponVisibilityConfig,
    setShowUpiIconsOnCartButton,
    setCartMarketingFlag,
    setCartUIConfigData
  }

  useEffect(() => {
    if (state.progressBar.milestones.length <= 0) {
      setIsProgressBarEnabled(false)
    }
  }, [state.progressBar.milestones.length, setIsProgressBarEnabled])

  const getters = {
    hasUnsavedCartWidgetChanges: useMemo(
      () => !compareStringConvertValue(state.initialProgressBar, state.progressBar),
      [state]
    ),
    hasUnsavedCartNoteChanges: useMemo(
      () => !compareStringConvertValue(state.initialCartNotes, state.cartNotes),
      [state]
    ),
    hasUnsavedCartUIChanges: useMemo(
      () => !compareStringConvertValue(state.initialCartUIData, state.cartUIData),
      [state]
    ),
    rawMilestones: state.progressBar.milestones,
    customizations: state.progressBar.customizations,
    milestones: state.progressBar.milestones.sort((a, b) => a.milestone_amount - b.milestone_amount),
    canCreateMilestone: state.progressBar.milestones.length < 5,
    canProgressBeEnabled: state.progressBar.milestones.length > 0,
    isAmountBased: state.progressBar.customizations.category === MilestoneCategory.PRICE_BASED,
    isCountBased: state.progressBar.customizations.category === MilestoneCategory.COUNT_BASED,
    colorPaletteData: state.cartUIData.colorPaletteData,
    cartBannerUIConfigData: state.cartUIData.cartBannerUIConfigData,
    cartWidthForMobile: state.cartUIData.cartWidthForMobile,
    cartBottomBannerUIConfigData: state.cartUIData.cartBottomBannerUIConfigData,
    cartNotes: state.cartNotes,
    initialCartNotes: state.initialCartNotes,
    cartNoteErrors: state.cartNoteErrors,
    cartUIData: state.cartUIData,
    showBannerErrors: state.showBannerErrors
  }

  useConfirmOnRouteChanges(
    getters.hasUnsavedCartUIChanges || getters.hasUnsavedCartWidgetChanges,
    () =>
      new Promise((resolve, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          resolve(true)
        }
        resolve(false)
      }),
    state
  )

  return <CartContext.Provider value={{ state, actions, getters }}>{children}</CartContext.Provider>
}

export function useCartContext() {
  if (!Boolean(CartContext)) throw new Error('useCartContext must be used within a CartContextProvider')
  const cartContext = useContext(CartContext)
  return cartContext
}

export default CartContextProvider
